import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Input, Select, Table } from 'antd';
import Button from '~/modules/components/global/button';
import Icon from '~/modules/components/global/icon';
import EmptyState from '~/modules/components/global/empty-state';
import {
	TableStyles
} from '~/modules/components/share-feedback/share-feedback-report/share-feedback-report.css';
import { Label } from '~/pages/common.css';
import confirm from '~/modules/components/global/confirm';
import ToastService from '~/modules/services/toast-service';
import ServiceAccountService from '~/modules/services/service-account-service';
import ApiTokenRevealModal from './api-token-reveal-modal';
import { captureError } from '../../../pixwel/exception-handler.factory';

const EXPIRY_OPTIONS = [
	{ label: '30 days', value: 30 },
	{ label: '60 days', value: 60 },
	{ label: '90 days', value: 90 },
	{ label: '180 days', value: 180 },
	{ label: '1 year', value: 365 }
];

const formatDate = (timestamp) => {
	if(!timestamp) {
		return '—';
	}
	return new Date(timestamp * 1000).toLocaleString();
};

const expiresAtFromDays = (days) => Math.floor(Date.now() / 1000) + days * 24 * 60 * 60;

const ServiceAccountTokens = ({ account }) => {
	const queryClient = useQueryClient();
	const queryKey = ['serviceAccountTokens', account._id];

	const [name, setName] = useState('');
	const [expiryDays, setExpiryDays] = useState(90);
	const [revealedToken, setRevealedToken] = useState(null);

	const { data: tokens = [], isLoading } = useQuery(
		queryKey,
		() => ServiceAccountService.listTokens(account._id),
		{ refetchOnWindowFocus: false }
	);

	const { mutateAsync: issueToken, isLoading: isIssuing } = useMutation(
		({ name, expiresAt }) => ServiceAccountService.issueToken(account._id, { name, expiresAt }),
		{
			onSuccess: (token) => {
				queryClient.invalidateQueries(queryKey);
				setRevealedToken(token);
				setName('');
				setExpiryDays(90);
			},
			onError: (err) => {
				const message = err?.response?.data?.error || err.message || 'Failed to issue token';
				ToastService.create('error', message);
				captureError(err, `Error issuing service account token: ${err.message}`);
			}
		}
	);

	const { mutateAsync: revokeToken } = useMutation(
		(tokenId) => ServiceAccountService.revokeToken(account._id, tokenId),
		{
			onSuccess: () => {
				queryClient.invalidateQueries(queryKey);
				ToastService.create('success', 'Token revoked');
			},
			onError: (err) => {
				ToastService.create('error', err?.response?.data?.error || 'Failed to revoke token');
				captureError(err, `Error revoking service account token: ${err.message}`);
			}
		}
	);

	const handleIssue = async() => {
		const trimmed = name.trim();
		if(!trimmed) {
			return;
		}
		await issueToken({ name: trimmed, expiresAt: expiresAtFromDays(expiryDays) });
	};

	const handleRevoke = async(row) => {
		// Named deliberately: this is shared infrastructure, and whoever revokes it may not be the
		// person who deployed whatever is using it.
		const ok = await confirm({
			title: 'Revoke service account token',
			content: `Revoke "${row.name}" on ${account.name}? Any integration using this token will `
				+ 'stop working immediately.',
			confirmText: 'Revoke',
			confirmVariant: 'danger',
			cancelText: 'Cancel',
			cancelVariant: 'secondary'
		});
		if(!ok) {
			return;
		}
		await revokeToken(row._id);
	};

	const columns = [
		{ title: 'Name', dataIndex: 'name', key: 'name', ellipsis: true },
		{
			title: 'Token',
			dataIndex: 'tokenPrefix',
			key: 'tokenPrefix',
			render: (prefix) => <code>svc_{prefix}…</code>
		},
		{ title: 'Created', dataIndex: 'createdAt', key: 'createdAt', render: formatDate },
		{ title: 'Last used', dataIndex: 'lastUsedAt', key: 'lastUsedAt', render: formatDate },
		{ title: 'Expires', dataIndex: 'expiresAt', key: 'expiresAt', render: formatDate },
		{
			title: 'Action',
			key: 'action',
			width: 140,
			render: (_value, row) => (
				<Button
					variant="danger"
					data-testid="service-account-token-revoke-button"
					onClick={() => handleRevoke(row)}
				>
					<Icon use="trash" color="white" /> Revoke
				</Button>
			)
		}
	];

	return (
		<div data-testid={`service-account-tokens-${account._id}`}>
			{!account.disabledAt && (
				<div className="create-form">
					<div className="name-field">
						<Label>Token name</Label>
						<Input
							placeholder="e.g. nightly-sync"
							value={name}
							onChange={(e) => setName(e.target.value)}
							data-testid="service-account-token-name-input"
						/>
					</div>
					<div className="expiry-field">
						<Label>Expires in</Label>
						<Select
							style={{ width: '100%' }}
							value={expiryDays}
							onChange={setExpiryDays}
							options={EXPIRY_OPTIONS}
							data-testid="service-account-token-expiry-select"
						/>
					</div>
					<div>
						<Button
							onClick={handleIssue}
							loading={isIssuing}
							disabled={!name.trim() || isIssuing}
							data-testid="service-account-token-issue-button"
						>
							Issue token
						</Button>
					</div>
				</div>
			)}
			<p className="rotation-hint">
				To rotate without downtime: issue a new token, deploy it, then revoke the old one.
			</p>
			<TableStyles>
				<Table
					columns={columns}
					dataSource={tokens.map((token) => ({ ...token, key: token._id }))}
					pagination={false}
					loading={isLoading}
					locale={{ emptyText: <EmptyState message="No active tokens" /> }}
				/>
			</TableStyles>
			<ApiTokenRevealModal
				token={revealedToken}
				onClose={() => setRevealedToken(null)}
				title="Service account token created"
				scopeWarning={`Anyone with this value can act as ${account.name} on the API.`}
			/>
		</div>
	);
};

ServiceAccountTokens.propTypes = {
	account: PropTypes.shape({
		_id: PropTypes.string.isRequired,
		name: PropTypes.string,
		disabledAt: PropTypes.number
	}).isRequired
};

export default ServiceAccountTokens;
